import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, ProfileContext }  from '../../App';
import { useTranslation } from 'react-i18next';
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, renderColumnHeader, TableProps, useReactTable } from '../Grid';
import { Button } from 'antd';
import Waiting from '../Waiting';
import {CacheKey, TimedLocalCache}  from '../../util/Cache';
import { formatDate } from '../../util/Format';
import {get, postJson} from '../../util/HttpRequest'
import { ErrorNotification } from '../Notification';
import GridSummary from '../GridSummary';
import GridPagination from '../GridPagination';
import ClaimDocumentUpload from './DocumentUpload';
import {  UploadIcon, SignatureIcon } from '../Icons';

class ClaimTaskData {
    claimTaskId: number = 0;
    claimId: number = 0;
    claimEventCreatedDateUTC: string = "";
    clientEventDescription: string = "";
    clientEventDocumentType: string = "";
    claimEventStatusCode: string = "";
    claimEventStatusDescription: string = "";
    claimEventId: number = 0;
    allowUpload: boolean = false;
    allowESignature: boolean = false;
    docuSignRequestId: number|null = null;
    docuSignEnvelopeId: string|null = null;
};

class ClaimTasksData {
    recordCount: number = 0;
    refreshDate: Date = new Date();
    searchResults: ClaimTaskData[] = [];
};

function ClaimTasks(props) {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [gridData, setGridData] = useState(new ClaimTasksData());
    const {accessToken, extendSession, user} = useContext(AuthContext);
    const {languagePreference, userProfileLossDraftsClaim} = useContext(ProfileContext);
    const [loadingDocuSign, setLoadingDocuSign] = useState(false);
    const [documentType, setDocumentType] = useState(false);
    const [claimEventId, setClaimEventId] = useState(false);
    const { t } = useTranslation(["ClaimTasks", "DataGrid"]);
    // Props used by renderColumnHeader() from Grid.tsx to control header UI:
    // Includes sorting labels and optional class names for styling
    const columnHeaderProps = [
        {
            id: 'claimEventCreatedDateUTC', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'clientEventDescription', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'claimEventStatusDescription', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        }
    ];

    useEffect (() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languagePreference, userProfileLossDraftsClaim.claimId, props.isClaimActive]);

    function refreshData() {
        setLoading(true);
        getData();
    };

    function getData() {
        extendSession();
        
        if (!props.isClaimActive) {
            setGridData(new ClaimTasksData());
            setErrorMsg("");
            setLoading(false);
            return;
        }
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimTasks");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());

        console.log("Fetch Claim Tasks from " + url);

        get(url, accessToken)
        .then( response => {
            if (!response.ok) { throw response }
            return response.json() as Promise<ClaimTaskData[]>
        })
        .then( searchResults => {
            if (searchResults.length > 0) {
                const completedDocuSignRequestIds = TimedLocalCache.getItem(CacheKey.COMPLETED_DOCUSIGN_REQUEST_ID)?.split(",");
                searchResults.filter(obj => obj.allowESignature === true).forEach(obj => {
                    
                    // Sometimes a document is eligible for signature but DS just hasn't created the envelope yet
                    // We want to give the user a message saying it will be ready soon
                    if (obj.docuSignRequestId !== null && obj.docuSignEnvelopeId === null) {
                        obj.allowESignature = false;
                        obj.claimEventStatusDescription = t("statusESignaturePreProcess");
                    }
                    
                    // After an embedded signing, it may take a few seconds/minutes for the status update from DS
                    // We want to give the user a message saying it will be marked completed soon
                    if (completedDocuSignRequestIds && completedDocuSignRequestIds.length > 0) {
                        if (completedDocuSignRequestIds.indexOf(obj.docuSignRequestId?.toString()) !== -1) {
                            obj.allowESignature = false;
                            obj.claimEventStatusDescription = t("statusESignaturePostProcess");
                        }
                    }
                });
            }
            
            const data = {
              recordCount: searchResults.length, 
              refreshDate: new Date(),
              searchResults: searchResults
            };
            setGridData(data);
            setErrorMsg("");
            setLoading(false);
        })
        .catch( error => {
            setGridData(new ClaimTasksData());
            setErrorMsg(error.message);
            setLoading(false);
        })
    };

    function handleDocumentUpload(event, curDocumentType, curClaimEventId) {
        if (event) {
            event.preventDefault();
        }
        extendSession();
        setDocumentType(curDocumentType);
        setClaimEventId(curClaimEventId);
        props.showUploadDocumentModal();
    }

    function handleDocumentSigning(event, docuSignRequestId, claimId, docuSignEnvelopeId) {
        event.preventDefault();
        extendSession();
        
        setLoadingDocuSign(true);
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/EmbeddedCeremony");
        console.log("Request embedded ceremony from " + url);

        const data = {
            DocuSignEnvelopeId: docuSignEnvelopeId,
            DocuSignRequestId: docuSignRequestId,
            SourceSystemCode: 'LD',
            SSOUserId: user.id
        };

        postJson(url, accessToken, JSON.stringify(data))
        .then((response) => {
            if (!response.ok) { throw new Error(response.status + " - " + response.statusText)}
            return response.text();
        })
        .then(response => {
            // the expected URL in our response should not contain any characters that need encoding
            // so if the encoded response URL is different from the original response URL, we may have XSS
            const hostname = (new URL(response)).hostname.toLowerCase();
            if (response === encodeURI(response) &&
                hostname.endsWith(import.meta.env.VITE_DOCUSIGN_BASE_HOSTNAME||"")
            ) {
                window.location.href = response;
            }
            else {
                setLoadingDocuSign(false);
                ErrorNotification("Error", "Unable to start signing ceremony");
            }
        })
        .catch(error => {
            setLoadingDocuSign(false);
            ErrorNotification("Error", error.message);
        })
        ;
    };

    function Table<TData extends object>({ columns, data }: TableProps<TData>) {
        const pageSize = Number(import.meta.env.VITE_GRID_PAGE_SIZE);
        // Use the state and functions returned from useReactTable to build your UI
        const table = useReactTable({
            data,
            columns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(), //load client-side pagination code
            getSortedRowModel: getSortedRowModel(),
            initialState: {
                pagination: {
                    pageIndex: 0, // Start on first page
                    pageSize: pageSize,
                },
            }, 
        });

        const filters = <></>;

        const pagination = (
            <GridPagination
                canPreviousPage={table.getCanPreviousPage()}
                canNextPage={table.getCanNextPage()}
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                gotoPage={table.setPageIndex}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
            />
        );

        const summary = (
            <GridSummary
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                pageSize={pageSize}
                recordCount={data.length}
                recordType={t('gridRecordType', { count: data.length })}
                refreshDate={gridData.refreshDate}
                pagination={pagination}
            />
        );

        return (
            <>
                {filters}
                <br />
                {summary}
                <table className="table" role="table">
                    <thead role="rowgroup">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} role="row">
                                {headerGroup.headers.map((header) =>
                                    renderColumnHeader(header, columnHeaderProps.find(x => x.id === header.column.id))
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody role="rowgroup">
                        {table.getRowModel().rows.map((row, i) => (
                            <React.Fragment key={row.id}>
                                <tr className={"row-" + (i % 2 === 0 ? "even" : "odd")} role="row" tabIndex={0}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} role="cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </>
        )
    }

    function renderDataGrid() {
        const columns = [{
            id: 'actions',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="datagrid-data-center">
                {
                    row.original.allowUpload && (
                        <Button type="link" className="icon-button" title={t('actionUpload')} 
                            onClick={(e) => handleDocumentUpload(e, row.original.clientEventDocumentType, row.original.claimEventId)}
                        >
                            <UploadIcon title={t('actionUpload')} />
                        </Button>
                    )
                }
                {
                    row.original.allowESignature && (
                        <Button type="link" className="icon-button" title={t('actionSign')} 
                            onClick={(e) => handleDocumentSigning(e, row.original.docuSignRequestId, row.original.claimId, row.original.docuSignEnvelopeId)}
                        >
                            <SignatureIcon title={t('actionSign')} />
                        </Button>
                    )
                }
                {
                    !row.original.allowUpload &&
                    !row.original.allowESignature && (
                        <span className="action-icon" />
                    )
                }
                </div>
            )
        }, {
            header: () => (<>{t('columnDate')}</>),
            accessorKey: 'claimEventCreatedDateUTC',
            enableSorting: true,
            cell: (info) => (
                <div className="datagrid-data-left">
                {
                    formatDate(info.getValue())
                }
                </div>
            )
        }, {
            header: () => (<>{t('columnDescription')}</>),
            accessorKey: 'clientEventDescription',
            enableSorting: true
        }, {
            header: () => (<>{t('columnStatus')}</>),
            accessorKey:'claimEventStatusDescription',
            enableSorting: true
        }];

        const table = <Table
            columns={columns}
            data={gridData.searchResults} 
        />;

        const modal = props.documentTypeSelectOptions.hasLoaded && props.documentUploadModalShow &&
        (
            <ClaimDocumentUpload 
                documentTypeSelectOptions={props.documentTypeSelectOptions}
                documentType={documentType}
                claimEventId={claimEventId}
                refreshData={refreshData} 
                hideModal={props.hideUploadDocumentModal}
            />
        );

        return <div>{table}{modal}</div>;
    };

    const title = <>
        {
            props.loadingDetails && 
            (
                <Waiting size='large' />
            )
        }
        {
            !props.loadingDetails && 
            (
                <>
                    <h2>
                        {props.isClaimActive ? t("title") : t("titleInactiveClaim")}
                    </h2>
                    {
                        !errorMsg && !loading && !loadingDocuSign && props.documentTypeSelectOptions.hasLoaded &&
                        (
                            <span>
                                <Button 
                                    className="button submit" 
                                    icon={<UploadIcon class="button-icon" title= {t('actionUpload')} />}
                                    type="primary"
                                    onClick={() => handleDocumentUpload(null, "", 0)}>
                                        {t('actionUpload')}
                                </Button>
                            </span>
                        )
                    }
                </>
            )
        }        
        <br /><br />
    </>;
    const contents = (errorMsg > '') ? <p><em>{errorMsg}</em></p> : (props.loadingDetails || loading || loadingDocuSign) ? <Waiting size='large' /> : renderDataGrid();
    return <div>{title}<br />{contents}</div>
};

export default ClaimTasks;